import { createIntl, createIntlCache, IntlShape } from '@formatjs/intl';
import { FormatXMLElementFn, PrimitiveType } from 'intl-messageformat';
import { Options as IntlMessageFormatOptions } from 'intl-messageformat/src/core';
import React, { ReactNode, useMemo } from 'react';
import { BBUILocale } from '../types';
import { makeBBUILocale } from '../utils';
import { useBBUILocale } from './useBBUILocale';

const intlCache = createIntlCache();

type ConstrainedMessageDescriptor<K extends string> = {
    id?: K;
    description?: string | object;
    defaultMessage?: string;
};

/**
 * Override the type of the formatMessage method so that only valid message Ids can be passed in.  It
 * will cause a compile error if an ID is used that is not found in the component's message bundle.
 */
type ConstrainedIntl<K extends string> = Omit<IntlShape<ReactNode>, 'formatMessage'> & {
    formatMessage: (
        descriptor: ConstrainedMessageDescriptor<K>,
        values?: Record<string, PrimitiveType | ReactNode | FormatXMLElementFn<string, ReactNode>>,
        opts?: IntlMessageFormatOptions
    ) => string;
};

/**
 * Hook to provide a handle to an intl object that can be used to localize messages
 * @param messageBundle a map from locale to message bundles that can be used by the formatjs library
 * @param localeOverride value to override the locale in the nearest context provider
 * @param messagesOverride object to override individual messages by message ID. Regardless of the selected locales, labels provided in this object will always take precedence.
 */
export function useIntl<K extends string>(
    messageBundle: Record<BBUILocale, Record<K, string>>,
    localeOverride?: string,
    messagesOverride?: Partial<Record<K, string>>
): { intl: ConstrainedIntl<K>; locale: string } {
    const { bbLocale: contextLocale, locale: rawLocale } = useBBUILocale();
    const locale = localeOverride ?? rawLocale;
    const bbLocale = makeBBUILocale(locale) || contextLocale;

    const intl = useMemo(
        () =>
            createIntl<React.ReactNode>(
                {
                    locale,
                    messages: { ...messageBundle[bbLocale], ...messagesOverride },
                },
                intlCache
            ),
        [bbLocale, locale, messagesOverride, messageBundle]
    );
    return { intl, locale };
}
